import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Sparkles, ArrowRight, Search, Users, Camera, Image, Heart } from 'lucide-react';

function WelcomePage() {
  const navigate = useNavigate();
  const displayName = localStorage.getItem('display_name');
  const token = localStorage.getItem('token');

  useEffect(() => {
    // Redirect to pricing if not logged in
    if (!token) {
      navigate('/pricing');
    }
  }, [token, navigate]);

  const handleContinue = () => {
    // Mark that user has seen the welcome page
    localStorage.setItem('hasSeenWelcome', 'true');
    // Navigate to Daily Essentials page next
    navigate('/daily-essentials');
  };

  if (!token) return null;

  return (
    <div className="min-h-screen hero-gradient relative overflow-hidden">
      {/* Butterfly Animation Styles - must be before butterflies */}
      <style>{`
        .butterfly-float-1 {
          animation: floatButterfly1 8s ease-in-out infinite;
        }
        .butterfly-float-2 {
          animation: floatButterfly2 10s ease-in-out infinite;
        }
        .butterfly-float-3 {
          animation: floatButterfly3 12s ease-in-out infinite;
        }
        .butterfly-float-4 {
          animation: floatButterfly1 9s ease-in-out infinite;
          animation-delay: 3s;
        }
        .butterfly-float-5 {
          animation: floatButterfly2 11s ease-in-out infinite;
          animation-delay: 5s;
        }
        
        .wings-flap {
          animation: flapWings 0.3s ease-in-out infinite;
          transform-origin: center;
        }
        
        @keyframes floatButterfly1 {
          0%, 100% {
            transform: translate(0, 0) rotate(0deg);
          }
          25% {
            transform: translate(60px, -25px) rotate(8deg);
          }
          50% {
            transform: translate(100px, 15px) rotate(-5deg);
          }
          75% {
            transform: translate(40px, -30px) rotate(10deg);
          }
        }
        
        @keyframes floatButterfly2 {
          0%, 100% {
            transform: translate(0, 0) rotate(0deg);
          }
          25% {
            transform: translate(-50px, 30px) rotate(-8deg);
          }
          50% {
            transform: translate(-90px, -20px) rotate(5deg);
          }
          75% {
            transform: translate(-35px, 35px) rotate(-10deg);
          }
        }
        
        @keyframes floatButterfly3 {
          0%, 100% {
            transform: translate(0, 0) rotate(0deg);
          }
          33% {
            transform: translate(-40px, -40px) rotate(12deg);
          }
          66% {
            transform: translate(50px, -20px) rotate(-8deg);
          }
        }
        
        @keyframes flapWings {
          0%, 100% {
            transform: scaleX(1);
          }
          50% {
            transform: scaleX(0.3);
          }
        }
      `}</style>

      {/* Animated Butterflies at the top */}
      <div className="absolute top-0 left-0 right-0 h-64 pointer-events-none">
        {/* Butterfly 1 - Pink */}
        <div className="butterfly-float-1 absolute" style={{ top: '15%', left: '8%' }}>
          <svg width="44" height="34" viewBox="0 0 32 24">
            <g className="wings-flap">
              <ellipse cx="8" cy="10" rx="7" ry="9" fill="#F9A8D4" opacity="0.9"/>
              <ellipse cx="24" cy="10" rx="7" ry="9" fill="#F9A8D4" opacity="0.9"/>
              <ellipse cx="8" cy="16" rx="5" ry="6" fill="#FBCFE8" opacity="0.9"/>
              <ellipse cx="24" cy="16" rx="5" ry="6" fill="#FBCFE8" opacity="0.9"/>
            </g>
            <ellipse cx="16" cy="12" rx="1.5" ry="8" fill="#374151"/>
            <circle cx="16" cy="5" r="2" fill="#374151"/>
            <path d="M14 3 Q12 0 10 2" stroke="#374151" strokeWidth="1" fill="none"/>
            <path d="M18 3 Q20 0 22 2" stroke="#374151" strokeWidth="1" fill="none"/>
          </svg>
        </div>
        
        {/* Butterfly 2 - Yellow */}
        <div className="butterfly-float-2 absolute" style={{ top: '25%', right: '12%' }}>
          <svg width="40" height="30" viewBox="0 0 32 24">
            <g className="wings-flap" style={{ animationDuration: '0.25s' }}>
              <ellipse cx="8" cy="10" rx="7" ry="9" fill="#FDE047" opacity="0.9"/>
              <ellipse cx="24" cy="10" rx="7" ry="9" fill="#FDE047" opacity="0.9"/>
              <ellipse cx="8" cy="16" rx="5" ry="6" fill="#FEF08A" opacity="0.9"/>
              <ellipse cx="24" cy="16" rx="5" ry="6" fill="#FEF08A" opacity="0.9"/>
            </g>
            <ellipse cx="16" cy="12" rx="1.5" ry="8" fill="#374151"/>
            <circle cx="16" cy="5" r="2" fill="#374151"/>
            <path d="M14 3 Q12 0 10 2" stroke="#374151" strokeWidth="1" fill="none"/>
            <path d="M18 3 Q20 0 22 2" stroke="#374151" strokeWidth="1" fill="none"/>
          </svg>
        </div>
        
        {/* Butterfly 3 - White */}
        <div className="butterfly-float-3 absolute" style={{ top: '40%', left: '40%' }}>
          <svg width="36" height="28" viewBox="0 0 32 24">
            <g className="wings-flap" style={{ animationDuration: '0.35s' }}>
              <ellipse cx="8" cy="10" rx="7" ry="9" fill="#FFFFFF" opacity="0.95"/>
              <ellipse cx="24" cy="10" rx="7" ry="9" fill="#FFFFFF" opacity="0.95"/>
              <ellipse cx="8" cy="16" rx="5" ry="6" fill="#F3F4F6" opacity="0.95"/>
              <ellipse cx="24" cy="16" rx="5" ry="6" fill="#F3F4F6" opacity="0.95"/>
            </g>
            <ellipse cx="16" cy="12" rx="1.5" ry="8" fill="#374151"/>
            <circle cx="16" cy="5" r="2" fill="#374151"/>
            <path d="M14 3 Q12 0 10 2" stroke="#374151" strokeWidth="1" fill="none"/>
            <path d="M18 3 Q20 0 22 2" stroke="#374151" strokeWidth="1" fill="none"/>
          </svg>
        </div>

        {/* Butterfly 4 - Orange */}
        <div className="butterfly-float-4 absolute" style={{ top: '20%', right: '35%' }}>
          <svg width="34" height="26" viewBox="0 0 32 24">
            <g className="wings-flap" style={{ animationDuration: '0.28s' }}>
              <ellipse cx="8" cy="10" rx="7" ry="9" fill="#FB923C" opacity="0.9"/>
              <ellipse cx="24" cy="10" rx="7" ry="9" fill="#FB923C" opacity="0.9"/>
              <ellipse cx="8" cy="16" rx="5" ry="6" fill="#FDBA74" opacity="0.9"/>
              <ellipse cx="24" cy="16" rx="5" ry="6" fill="#FDBA74" opacity="0.9"/>
            </g>
            <ellipse cx="16" cy="12" rx="1.5" ry="8" fill="#374151"/>
            <circle cx="16" cy="5" r="2" fill="#374151"/>
            <path d="M14 3 Q12 0 10 2" stroke="#374151" strokeWidth="1" fill="none"/>
            <path d="M18 3 Q20 0 22 2" stroke="#374151" strokeWidth="1" fill="none"/>
          </svg>
        </div>

        {/* Butterfly 5 - Light Purple */}
        <div className="butterfly-float-5 absolute" style={{ top: '35%', left: '20%' }}>
          <svg width="38" height="28" viewBox="0 0 32 24">
            <g className="wings-flap" style={{ animationDuration: '0.32s' }}>
              <ellipse cx="8" cy="10" rx="7" ry="9" fill="#C4B5FD" opacity="0.9"/>
              <ellipse cx="24" cy="10" rx="7" ry="9" fill="#C4B5FD" opacity="0.9"/>
              <ellipse cx="8" cy="16" rx="5" ry="6" fill="#DDD6FE" opacity="0.9"/>
              <ellipse cx="24" cy="16" rx="5" ry="6" fill="#DDD6FE" opacity="0.9"/>
            </g>
            <ellipse cx="16" cy="12" rx="1.5" ry="8" fill="#374151"/>
            <circle cx="16" cy="5" r="2" fill="#374151"/>
            <path d="M14 3 Q12 0 10 2" stroke="#374151" strokeWidth="1" fill="none"/>
            <path d="M18 3 Q20 0 22 2" stroke="#374151" strokeWidth="1" fill="none"/>
          </svg>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16 max-w-3xl relative z-10">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-8 md:p-12 border border-primary/10">
          
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
              <Sparkles className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
              Welcome to Radiant 40!
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-primary via-accent to-primary mx-auto rounded-full"></div>
          </div>

          {/* Introduction */}
          <div className="prose prose-lg max-w-none text-secondary-700 space-y-6">
            <p className="text-lg leading-relaxed">
              Congratulations on taking a transformative step towards enhancing your confidence and vitality by enrolling in this 12-week program. This journey is designed to empower you with renewed energy and self-assurance.
            </p>

            {/* Program Structure */}
            <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl p-6 border border-primary/10">
              <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">
                Program Structure:
              </h2>
              <p className="leading-relaxed">
                The course is organized into 12 weekly themes, each introducing a new daily task to incorporate into your routine. You'll start by establishing a strong foundation through goal-setting. Every evening, devote 5 to 15 minutes to journaling about the day's completed task and strategize how to integrate this activity into your life.
              </p>
            </div>

            {/* Building Your 19 Essential Tasks */}
            <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-6 border border-violet-200">
              <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">
                🌟 Building Your 19 Essential Daily Tasks
              </h2>
              <p className="leading-relaxed mb-4">
                Throughout this program, you will build a personalized list of <strong>19 essential daily tasks</strong> unique to your radiance journey:
              </p>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-2">
                  <span className="text-violet-600 font-bold">6</span>
                  <span>Core daily essentials (morning routine, hydration, self-care)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-violet-600 font-bold">1</span>
                  <span>Nightly journaling reflection</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-violet-600 font-bold">12</span>
                  <span>Personal essentials YOU choose (1 new one added each week!)</span>
                </li>
              </ul>
              <p className="leading-relaxed text-secondary-600 italic">
                Each week, you'll add one new personal essential to your checklist based on what resonates most with you from that week's theme. By Week 12, you'll have crafted a complete daily routine tailored specifically to YOUR needs and goals!
              </p>
            </div>

            {/* Initial Reflections */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200">
              <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">
                Initial Reflections:
              </h2>
              <p className="leading-relaxed">
                In the initial days, take time to contemplate the potential benefits of these new activities in your daily life. Visualize how they can positively impact your well-being.
              </p>
            </div>

            {/* Nightly Journaling */}
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-6 border border-indigo-200">
              <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">
                📝 Your Nightly Journaling Practice
              </h2>
              <p className="leading-relaxed mb-4">
                Each evening, spend 5-15 minutes reflecting in your journal. Your journaling should include:
              </p>
              <ul className="space-y-3 mb-4">
                <li className="flex items-start gap-3">
                  <span className="bg-indigo-200 text-indigo-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">1</span>
                  <span>How you completed today's task and what you learned</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-indigo-200 text-indigo-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">2</span>
                  <span><strong>Which tasks from previous weeks</strong> are you still implementing daily?</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-indigo-200 text-indigo-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">3</span>
                  <span>How can you incorporate more tasks more often?</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-indigo-200 text-indigo-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">4</span>
                  <span>Benefits you're noticing from your growing routine</span>
                </li>
              </ul>
              <div className="bg-white/60 rounded-xl p-4 border border-indigo-100">
                <p className="text-secondary-700 text-sm">
                  <strong>💡 Remember:</strong> While some essentials are non-negotiable, you'll find that incorporating many of these tasks <em>even just a few times a week</em> will result in long-lasting and noticeable benefits. Not everything needs to be daily—find the rhythm that works for YOU.
                </p>
              </div>
            </div>

            {/* Commitment and Growth */}
            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl p-6 border border-amber-200">
              <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">
                Commitment and Growth:
              </h2>
              <p className="leading-relaxed">
                As you diligently engage with these daily tasks, you will soon notice a shift. Within a few weeks, your journaling will evolve into reflections on the tangible benefits you're experiencing. Expect substantial improvements in mental clarity, emotional resilience, and physical well-being. Watch as your vitality and appearance transform, reflecting the powerful journey you are undertaking.
              </p>
            </div>

            {/* Life Happens - Encouragement */}
            <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-6 border border-rose-200">
              <div className="flex items-start gap-4">
                <div className="bg-rose-200 p-3 rounded-full shrink-0">
                  <Heart className="w-6 h-6 text-rose-700" />
                </div>
                <div>
                  <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-3">
                    🌸 A Gentle Reminder
                  </h2>
                  <p className="leading-relaxed mb-3">
                    This program works best when you show up for yourself every day. Consistency is where the magic happens—small daily actions create remarkable transformations over 12 weeks.
                  </p>
                  <p className="leading-relaxed mb-3">
                    But here's the truth: <strong>life happens</strong>. Some days will be harder than others. You might miss a day—or even a few. And that's okay.
                  </p>
                  <p className="leading-relaxed font-medium text-rose-800">
                    If you stumble, don't give up. Simply pick up where you left off and jump right back in. Your radiant journey will be right here waiting for you, with open arms and no judgment. What matters most is that you keep coming back. 💕
                  </p>
                </div>
              </div>
            </div>

            {/* Your First Task - Before Photo */}
            <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-6 border border-pink-200">
              <div className="flex items-start gap-4">
                <div className="bg-pink-200 p-3 rounded-full shrink-0">
                  <Camera className="w-6 h-6 text-pink-700" />
                </div>
                <div>
                  <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-3">
                    📸 Your First Task: Capture Your "Before"
                  </h2>
                  <p className="leading-relaxed mb-3">
                    Before you begin Day 1, take a moment to capture your starting point. Take a selfie or ask someone to photograph you—this is your "before" image. You don't need to share it with anyone.
                  </p>
                  <p className="leading-relaxed text-secondary-600 italic">
                    In 12 weeks, you'll take an "after" photo and be amazed at your transformation—not just in appearance, but in the confidence radiating from within.
                  </p>
                </div>
              </div>
            </div>

            {/* Search Feature */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
              <div className="flex items-start gap-4">
                <div className="bg-blue-200 p-3 rounded-full shrink-0">
                  <Search className="w-6 h-6 text-blue-700" />
                </div>
                <div>
                  <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-3">
                    🔍 Revisit Any Challenge Anytime
                  </h2>
                  <p className="leading-relaxed mb-3">
                    As you progress through the program, you can always revisit past challenges using the <strong>Search feature</strong> in the navigation menu. Simply click on "Search Tasks" to find any previous day's content.
                  </p>
                  <p className="leading-relaxed text-secondary-600">
                    <em>Why revisit?</em> Some days you might want to refresh a technique, review a recipe, or revisit a journaling prompt that resonated with you. Your journey content is always there for you!
                  </p>
                </div>
              </div>
            </div>

            {/* Community Board */}
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl p-6 border border-purple-200">
              <div className="flex items-start gap-4">
                <div className="bg-purple-200 p-3 rounded-full shrink-0">
                  <Users className="w-6 h-6 text-purple-700" />
                </div>
                <div>
                  <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-3">
                    💬 Join Our Supportive Community
                  </h2>
                  <p className="leading-relaxed mb-3">
                    You're not alone on this journey! Our <strong>Community Board</strong> is a space where women just like you share their experiences, celebrate wins, seek support, and encourage one another.
                  </p>
                  <p className="leading-relaxed mb-3">
                    Share your achievements, struggles, questions, and victories. Whether you nailed a new yoga pose, cooked an amazing rainbow meal, or simply made it through a challenging day—your community wants to hear about it!
                  </p>
                  <p className="leading-relaxed text-secondary-600">
                    <em>Find it:</em> Click "Community" in the navigation menu or the Community button on your dashboard.
                  </p>
                </div>
              </div>
            </div>

            {/* Photo Encouragement */}
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-6 border border-orange-200">
              <div className="flex items-start gap-4">
                <div className="bg-orange-200 p-3 rounded-full shrink-0">
                  <Camera className="w-6 h-6 text-orange-700" />
                </div>
                <div>
                  <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-3">
                    📷 Document Your Journey with Photos
                  </h2>
                  <p className="leading-relaxed mb-3">
                    We encourage you to take <strong>lots of photos</strong> throughout your 12-week transformation! Capture moments like:
                  </p>
                  <ul className="list-disc list-inside space-y-2 mb-4 text-secondary-700">
                    <li>Your beautiful <strong>meal creations</strong> 🥗</li>
                    <li>Your <strong>yoga poses</strong> and workout victories 🧘‍♀️</li>
                    <li>Your <strong>vision board</strong> and creative projects 🎨</li>
                    <li><strong>Before and after</strong> comparisons 📸</li>
                    <li>Moments of <strong>self-care</strong> and relaxation 🌸</li>
                    <li>Your growing <strong>confidence</strong> shining through! ✨</li>
                  </ul>
                  <p className="leading-relaxed font-medium text-orange-800">
                    Share your photos on the Community Board often—your journey can inspire others, and seeing each other's progress creates accountability and motivation!
                  </p>
                </div>
              </div>
            </div>

            {/* Private Gallery */}
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-6 border border-teal-200">
              <div className="flex items-start gap-4">
                <div className="bg-teal-200 p-3 rounded-full shrink-0">
                  <Image className="w-6 h-6 text-teal-700" />
                </div>
                <div>
                  <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-3">
                    🔒 Your Private Photo Gallery
                  </h2>
                  <p className="leading-relaxed mb-3">
                    Not ready to share everything publicly? No problem! You have a <strong>Private Gallery</strong> where you can save photos just for yourself. Keep your before/after photos, personal progress shots, or any images you want to treasure privately.
                  </p>
                  <p className="leading-relaxed text-secondary-600">
                    <em>Find it:</em> Navigate to "My Gallery" in the menu. Only you can see these photos—they're 100% private.
                  </p>
                </div>
              </div>
            </div>

            {/* Support Each Other */}
            <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-6 border border-rose-200">
              <div className="flex items-start gap-4">
                <div className="bg-rose-200 p-3 rounded-full shrink-0">
                  <Heart className="w-6 h-6 text-rose-700" />
                </div>
                <div>
                  <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-3">
                    💕 Support One Another
                  </h2>
                  <p className="leading-relaxed">
                    After completing each day's task, you'll have the opportunity to share your experience on the Community Board. Celebrate your wins, share your struggles, ask for advice, and most importantly—<strong>encourage your fellow radiant women</strong>. When you see others' posts, leave a like or supportive comment. Together, we rise!
                  </p>
                </div>
              </div>
            </div>

            {/* Closing */}
            <p className="text-xl text-center font-serif italic text-secondary-800 pt-4">
              Embrace this commitment and witness the remarkable changes in your life.
            </p>
          </div>

          {/* CTA Button */}
          <div className="mt-10 text-center">
            <Button
              onClick={handleContinue}
              data-testid="begin-journey-btn"
              className="bg-primary hover:bg-primary/90 text-white px-10 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 inline-flex items-center gap-3"
            >
              Begin My Journey
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WelcomePage;
